import { useQueryClient } from '@tanstack/react-query';
import { MessageSquareOff, RotateCw, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { candidateJobQueryKeys } from '@/modules/candidates/queries/queryKeys';
import type { CandidateJobApi } from '@/modules/candidates/types';
import { candidateFullName } from '@/modules/hiring-manager/utils/candidateDisplay';
import type { JobApi } from '@/modules/jobs/types';
import { useSendDraftMessagesMutation } from '@/modules/messaging/mutations/useSendDraftMessagesMutation';
import { useUpdateMessageMutation } from '@/modules/messaging/mutations/useUpdateMessageMutation';
import { messagingQueryKeys } from '@/modules/messaging/queries/queryKeys';
import { useMessagesQuery } from '@/modules/messaging/queries/useMessagesQuery';
import { Button } from '@/modules/ui/components/button/Button';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/modules/ui/components/sheet/Sheet';
import type { LinkedInStatus } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import { actionErrorMessage } from '@/utils/actionErrorMessage';
import { getInitials } from '@/utils/getInitials';

import { DraftEditor, MESSAGE_LIMIT } from './DraftEditor';
import { DraftTemplateSection } from './DraftTemplateSection';
import { LinkedInNotice } from './LinkedInNotice';

interface OutreachDraftsSheetProps {
  job: JobApi;
  /** Candidates whose reach-out message is still a draft. */
  drafts: CandidateJobApi[];
  /** Finlay sends from the customer's LinkedIn; without it nothing goes out. */
  linkedInStatus: LinkedInStatus;
  onRetryLinkedIn: () => void;
  /** Where LinkedIn gets connected; absent when this workspace cannot. */
  integrationsHref?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const DRAFT_STATUSES = ['Draft' as const];

const pluralize = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * Every draft Finlay wrote for this job: a list to tick people in or out,
 * and an editor for the one in focus. Ticked drafts are sent together with
 * `PUT /messages/send`; skipped ones stay drafts. Edits are saved just before
 * sending so a customer never loses a tweak. The job's template is shown as
 * what Finlay is told to write; changing it affects the next drafts, not these.
 */
export function OutreachDraftsSheet({
  job,
  drafts,
  linkedInStatus,
  onRetryLinkedIn,
  integrationsHref,
  isOpen,
  onOpenChange,
}: OutreachDraftsSheetProps) {
  const queryClient = useQueryClient();
  const updateMessageMutation = useUpdateMessageMutation();
  const sendMutation = useSendDraftMessagesMutation();
  // Unsaved text per message id; absent means the server text is shown.
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Message ids the customer unticked; everything else goes out on Send.
  const [skippedIds, setSkippedIds] = useState<string[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  // Stays in the open sheet next to Send, so the user can retry or untick.
  const [sendError, setSendError] = useState<string | null>(null);

  // This job's drafts on the user's LinkedIn account in one request, matched
  // to candidates by message id. The thread endpoints are no use here:
  // `GET /message-threads/{id}` leaves drafts out of its messages and the
  // thread list hides draft-only threads. Loaded while the job has drafts,
  // so the sheet opens with the text already there.
  const draftsQuery = useMessagesQuery(
    { statuses: DRAFT_STATUSES, jobId: job.id },
    { enabled: drafts.length > 0 },
  );
  const textById = new Map(
    (draftsQuery.data ?? []).map((message) => [message.id, message.content]),
  );

  const items = drafts.flatMap((candidateJob) => {
    const message = candidateJob.reachOutMessage;
    if (!message) return [];
    const serverText = textById.get(message.id);
    // Sent or deleted since the candidate list loaded: nothing to review.
    if (draftsQuery.isSuccess && serverText === undefined) return [];
    const edit = edits[message.id];
    return [
      {
        candidateJob,
        message,
        text: edit ?? serverText ?? '',
        isEdited: edit !== undefined && edit !== serverText,
        isLoaded: serverText !== undefined,
        isSkipped: skippedIds.includes(message.id),
      },
    ];
  });
  type Item = (typeof items)[number];

  const toSend = items.filter((item) => !item.isSkipped);
  const skippedCount = items.length - toSend.length;
  const tooLong = toSend.find((item) => item.text.length > MESSAGE_LIMIT);
  const canSend =
    toSend.length > 0 &&
    !draftsQuery.isError &&
    toSend.every((item) => item.isLoaded && item.text.trim() !== '') &&
    !tooLong &&
    linkedInStatus === 'connected';

  const focusedIndex = Math.max(
    0,
    items.findIndex((item) => item.message.id === focusedId),
  );
  const focused = items[focusedIndex];
  const previous = items[focusedIndex - 1];
  const next = items[focusedIndex + 1];

  const toggle = (item: Item) =>
    setSkippedIds((ids) =>
      item.isSkipped
        ? ids.filter((id) => id !== item.message.id)
        : [...ids, item.message.id],
    );
  const isAllSelected = skippedCount === 0;

  const changeOpen = (open: boolean) => {
    if (!open) setSendError(null);
    onOpenChange(open);
  };

  const send = async () => {
    setIsSending(true);
    setSendError(null);
    try {
      await Promise.all(
        toSend
          .filter((item) => edits[item.message.id] !== undefined)
          .map((item) =>
            updateMessageMutation.mutateAsync({
              threadId: item.message.messageThreadId,
              messageId: item.message.id,
              message: { subject: null, content: item.text },
            }),
          ),
      );
      await sendMutation.mutateAsync({
        messageIds: toSend.map((item) => item.message.id),
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: candidateJobQueryKeys.lists(job.id),
        }),
        queryClient.invalidateQueries({
          queryKey: messagingQueryKeys.messages(DRAFT_STATUSES),
        }),
      ]);
      setEdits({});
      onOpenChange(false);
      toast.success(
        `${pluralize(toSend.length, 'message', 'messages')} queued. Finlay sends them from your LinkedIn over the next few hours.`,
      );
    } catch (error) {
      setSendError(actionErrorMessage('send the messages', error));
    } finally {
      setIsSending(false);
    }
  };

  const renderBody = () => {
    if (items.length === 0) {
      return (
        <p className="text-muted-foreground flex-1 px-6 py-8 text-center text-[13px]">
          No drafts waiting. Tick people in Sourced or Shortlisted and choose
          Draft messages.
        </p>
      );
    }
    if (draftsQuery.isError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
          <span className="bg-destructive/10 text-destructive grid size-10 place-items-center rounded-full">
            <MessageSquareOff className="size-5" />
          </span>
          <div className="flex max-w-sm flex-col gap-1">
            <p className="font-semibold">We couldn&apos;t load your drafts</p>
            <p className="text-[13px] text-neutral-500">
              Your {pluralize(items.length, 'draft is', 'drafts are')} saved and
              nothing was sent. This is usually temporary, so try again in a
              moment.
            </p>
          </div>
          <Button
            className="h-8 rounded-md"
            loading={draftsQuery.isFetching}
            onClick={() => void draftsQuery.refetch()}
            size="sm"
            variant="outline"
          >
            <RotateCw className="size-3.5" />
            Try again
          </Button>
        </div>
      );
    }
    return (
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div className="flex max-h-48 shrink-0 flex-col overflow-y-auto border-b p-2 md:max-h-none md:w-64 md:border-r md:border-b-0">
          <label className="flex cursor-pointer items-center gap-3 border-b px-2 pt-1 pb-2.5 text-[13px] text-neutral-500">
            <Checkbox
              aria-label="Select all drafts"
              checked={
                isAllSelected
                  ? true
                  : toSend.length > 0
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={() =>
                setSkippedIds(
                  isAllSelected ? items.map((item) => item.message.id) : [],
                )
              }
            />
            {toSend.length} of {items.length} selected
          </label>
          <ul className="flex flex-col gap-0.5 pt-1">
            {items.map((item) => {
              const name = candidateFullName(item.candidateJob);
              const isFocused = item === focused;
              return (
                <li
                  className={cn(
                    'flex items-center gap-3 rounded-md pl-2 transition-colors duration-100',
                    isFocused ? 'bg-muted' : 'hover:bg-accent',
                  )}
                  key={item.message.id}
                >
                  <Checkbox
                    aria-label={`Send to ${name}`}
                    checked={!item.isSkipped}
                    onCheckedChange={() => toggle(item)}
                  />
                  <button
                    aria-current={isFocused ? 'true' : undefined}
                    className={cn(
                      'flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-2 pr-2 text-left text-[13px]',
                      item.isSkipped && 'text-neutral-500',
                    )}
                    onClick={() => setFocusedId(item.message.id)}
                    type="button"
                  >
                    <span className="bg-background grid size-6 shrink-0 place-items-center rounded-full border text-[10px] font-semibold text-neutral-500">
                      {getInitials(name)}
                    </span>
                    <span className="truncate font-medium">{name}</span>
                    {(item.isSkipped || item.isEdited) && (
                      <span className="ml-auto shrink-0 text-xs text-neutral-500">
                        {item.isSkipped ? 'Skipped' : 'Edited'}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {focused && (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <DraftEditor
              candidateJob={focused.candidateJob}
              isLoading={!focused.isLoaded}
              onChange={(content) =>
                setEdits((current) => ({
                  ...current,
                  [focused.message.id]: content,
                }))
              }
              onNext={next && (() => setFocusedId(next.message.id))}
              onPrevious={previous && (() => setFocusedId(previous.message.id))}
              position={focusedIndex + 1}
              total={items.length}
              value={focused.text}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Sheet onOpenChange={changeOpen} open={isOpen}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 pt-5 pb-4">
          <SheetTitle className="text-lg">
            Review {pluralize(items.length, 'draft', 'drafts')}
          </SheetTitle>
          <SheetDescription>
            Read each message and edit it if you like. Untick anyone you&apos;d
            rather not contact.
          </SheetDescription>
        </SheetHeader>

        {renderBody()}

        {/* Below the drafts: it shapes the next batch, not the one in review.
            Keyed so an unsaved template edit never carries over to another job. */}
        <DraftTemplateSection job={job} key={job.id} />

        <SheetFooter className="flex-col items-stretch gap-3 border-t px-6 py-4 sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 flex-col text-[13px]">
            {sendError ? (
              <InlineError>{sendError}</InlineError>
            ) : linkedInStatus === 'connected' ||
              linkedInStatus === 'pending' ? (
              items.length > 0 &&
              !draftsQuery.isError && (
                <>
                  <span className="font-medium">
                    {tooLong
                      ? `Shorten the message to ${candidateFullName(tooLong.candidateJob)} to send`
                      : `${pluralize(toSend.length, 'message', 'messages')} ready to send from your LinkedIn`}
                  </span>
                  {skippedCount > 0 && (
                    <span className="text-neutral-500">
                      {pluralize(
                        skippedCount,
                        'skipped person stays',
                        'skipped people stay',
                      )}{' '}
                      in Drafts to review.
                    </span>
                  )}
                </>
              )
            ) : (
              <LinkedInNotice
                className="text-neutral-500"
                integrationsHref={integrationsHref}
                onRetry={onRetryLinkedIn}
                status={linkedInStatus}
              />
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              className="h-8 rounded-md"
              onClick={() => changeOpen(false)}
              size="sm"
              variant="ghost"
            >
              Close
            </Button>
            <Button
              className="h-8 rounded-md"
              disabled={!canSend}
              loading={isSending}
              onClick={() => void send()}
              size="sm"
            >
              <Send className="size-3.5" />
              {toSend.length > 0 && !draftsQuery.isError
                ? `Send ${toSend.length} from LinkedIn`
                : 'Send from LinkedIn'}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
